import { FetchApi } from "@/utils/FetchApi";
import { inject, provide } from "vue";
import { useSessionStore } from "@/composables/useSessionStore";

const sessionStore = useSessionStore();
const { getRecorderSessionId, getSummary } = sessionStore;

/**
 * Composable for session-related API operations
 */

interface ApiClient {
  post: (url: string, data?: any) => Promise<any>;
  put: (url: string, data?: any) => Promise<any>;
}

export interface CreateSessionParams {
  licenseId: number | string | null;
  professionalId: number | string | null;
  patientId: string | number | null;
  templateId: number;
  userId: string | number | null;
  analyticsDeviceId?: string;
  analyticsSessionId?: number;
  analyticsUserId?: string;
  pageCategory?: string;
  pageType?: string;
  pageSection?: string;
  pageUrl?: string;
  isAnalyticsBlockedOnClient?: boolean;
}

/**
 * Composable that provides session API functions
 * Must be called within a Vue component setup context to access injected apiClient
 * @returns Object with createSession function
 */
export function useSessionApi() {
  /**
   * Creates a new session via the API
   * @param params - Session creation parameters
   * @returns Promise resolving to the session data
   * @throws Error if the API call fails or apiClient is not available
   */
  const createSession = async (apiClient: ApiClient, params: CreateSessionParams): Promise<any> => {
    if (!apiClient) {
      throw new Error("Cannot create session: ApiClient is not available");
    }

    if (!params.licenseId || !params.professionalId || !params.patientId || !params.templateId) {
      throw new Error("Missing required parameters for session creation");
    }

    const optionalFields = {
      analyticsDeviceId: params.analyticsDeviceId,
      analyticsSessionId: params.analyticsSessionId,
      analyticsUserId: params.analyticsUserId,
      pageCategory: params.pageCategory,
      pageType: params.pageType,
      pageSection: params.pageSection,
      pageUrl: params.pageUrl,
      isAnalyticsBlockedOnClient: params.isAnalyticsBlockedOnClient,
    };

    // Keep defined values only. A literal `false` is a valid verdict and must be preserved
    // (only `undefined`/empty are dropped), so the backend can tell "reachable" from "unknown".
    const filteredOptional = Object.fromEntries(
      Object.entries(optionalFields).filter(([, v]) => v !== undefined && v !== "")
    );

    const payload = {
      licenseId: params.licenseId,
      professionalId: params.professionalId,
      patientId: params.patientId,
      templateId: params.templateId,
      userId: params.userId,
      ...filteredOptional,
    };

    console.log("[useSessionApi] createSession payload:", JSON.stringify(payload, null, 2));

    return await apiClient.post("/session/create", payload);
  };

  const SaveSummary = async (apiClient: ApiClient): Promise<any> => {
    if (!getRecorderSessionId() || !getSummary()) {
      throw new Error("Missing required parameters for summary saving");
    }

    const response = await apiClient.put(`/session/${getRecorderSessionId()}`, {
      summary: getSummary(),
      status: "Verified",
    });
    return response;
  };

  return {
    createSession,
    SaveSummary,
  };
}
